// Libraries
import { LayoutProps, SystemProps, TypographyProps } from '@chakra-ui/react';
// Types
import { RatingKeys } from '@globalTypes/index';

/**
 * Enum representing different sizes for the RatingInfoCompacted component.
 */
enum RatingInfoSizes {
  /**
   * Represents the medium size for the RatingInfoCompacted component.
   */
  md = 'md',
  /**
   * Represents the small size for the RatingInfoCompacted component.
   */
  sm = 'sm',
}

/**
 * Represents the keys of the RatingInfoSizes enum.
 */
type RatingInfoSizesKeys = keyof typeof RatingInfoSizes;

/**
 * Props for the RatingInfoCompacted component.
 */
export type RatingInfoCompactedProps = {
  /**
   * The rating key, which should be one of the keys defined in the RatingKeys enum.
   */
  rating: RatingKeys;
  /**
   * The description text for the rating.
   */
  description: string;
  /**
   * The loading state for the rating. Default value is false.
   */
  isLoading?: boolean;
  /**
   * Indicates whether only the description is in a loading state. Defaults to false.
   */
  isLoadingDescription?: boolean;
  /**
   * Indicates whether only the rating is in a loading state. Defaults to false.
   */
  isLoadingRating?: boolean;
  /**
   * The size of the component. Should be one of the sizes defined in the RatingInfoSizes enum.
   */
  ratingInfoSize?: RatingInfoSizesKeys;
  /**
   * The position of the description in the layout.
   */
  descriptionPosition?: SystemProps['flexDirection'];
  /**
   * The color of the description text.
   */
  descriptionColor?: string;
};

/**
 * Configuration object for different size variants of the RatingInfoCompacted component.
 */
type VariantsConfigType = {
  /**
   * Configuration for the medium size variant.
   */
  md: {
    /**
     * The box size for the medium size variant.
     */
    boxSize: LayoutProps['boxSize'];

    /**
     * The font size for the medium size variant.
     */
    fontSize: TypographyProps['fontSize'];
  };

  /**
   * Configuration for the small size variant.
   */
  sm: {
    /**
     * The box size for the small size variant.
     */
    boxSize: LayoutProps['boxSize'];

    /**
     * The font size for the small size variant.
     */
    fontSize: TypographyProps['fontSize'];
  };
};

/**
 * Configurations for different size variants of the RatingInfoCompacted component.
 */
export const VariantsConfig: VariantsConfigType = {
  /**
   * Configuration for the medium size variant.
   */
  md: {
    boxSize: 4,
    fontSize: { base: 's' },
  },

  /**
   * Configuration for the small size variant.
   */
  sm: {
    boxSize: 3,
    fontSize: { base: 'xs' },
  },
};
